/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionReset';
import { analyzeActionReset } from './ActionResetAnalyze';

const ActionResetManager: ActionManagerFn<'ActionReset'> = (
  platform: Platform,
): ActionManager<'ActionReset'> => {
  return {
    ...baseActionManager(platform, 'ActionReset'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
    },
    name: 'Reset Data Store',
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    getDisplayText: (item) => item.props.alias,
    icon: { iconName: 'undo' },
    replaceableWith: ['ActionRefresh', 'ActionClear', 'ActionSave'],
    analyze: analyzeActionReset,
    description: `**Overview**<br/>
The Reset Data Store action is a state management component designed to revert a Data Store to its original state. It effectively acts as an "Undo All" command, discarding any pending changes—such as edited fields, new rows, or deleted records—that have occurred since the data was last loaded or saved.<br/><br/>
This component is the standard implementation for "Cancel" buttons in forms. Instead of manually clearing every input field, this action targets the underlying data model. When triggered, it wipes out the "dirty" state of the store, causing all bound UI components (text boxes, grids, checkboxes) to immediately snap back to displaying their original values.

**Key Features**<br/>
* **Comprehensive Rollback:** Undoes all client-side modifications (Inserts, Updates, Deletes) on the target store, restoring it to the exact state it was in after the last successful fetch or save.
* **Broad Store Support:** Compatible with CloudDataStore, DataStore, LocalDataStore, and TransientDataStore, making it useful for both persistent database records and temporary local collections.
* **UI Synchronization:** Because it modifies the data store directly, all UI components bound to that store automatically update to reflect the reset data without additional logic.
* **Component Interchangeability:** Explicitly configured to be replaceable with ActionRefresh, ActionClear, or ActionSave, streamlining the workflow if the button's purpose changes.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property.
  * *Function:* Identifies the specific Data Store to be reset.
  * *Constraint:* Restricts selection to valid data store types (Cloud, Local, Transient, etc.).

**Use Cases**<br/>
* **Form Cancellation:** A "Cancel" button on a User Profile form that discards any typed changes and returns the fields to the currently saved profile data.
* **Draft Management:** A "Discard Draft" button in a note-taking app that reverts the TransientDataStore holding the current note back to empty or its previous version.
* **Error Recovery:** Automatically resetting a store if a validation check fails significantly, ensuring the user starts from a clean slate.`,
  };
};

export default ActionResetManager;
